import { Dayjs } from 'dayjs';
import { t } from 'i18next';

import { FeatureFlags } from '~constants/featureFlags';
import { Nullable } from '~types/Nullable';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

interface UseWorkLockProps {
  billingApprovedAt: Nullable<Dayjs>;
  editable: Nullable<boolean>;
  fieldApprovedAt: Nullable<Dayjs>;
  // Without the edit permission `editable` is also false, for a reason the lock text would misstate.
  hasEditPermission: boolean;
}

interface WorkLock {
  isLocked: boolean;
  reason: Nullable<string>;
}

// The API sends only `editable`, so a lock with no approval timestamp is assumed to come from a settlement.
export default function useWorkLock({
  billingApprovedAt,
  editable,
  fieldApprovedAt,
  hasEditPermission,
}: UseWorkLockProps): WorkLock {
  const chargeBasedSettlementsEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.chargeBasedSettlements,
  });

  const isLocked =
    chargeBasedSettlementsEnabled && hasEditPermission && editable === false;

  if (!isLocked) {
    return { isLocked: false, reason: null };
  }

  const isApproved = Boolean(billingApprovedAt || fieldApprovedAt);

  return {
    isLocked,
    reason: isApproved ? t('work_lock.approved') : t('work_lock.settled'),
  };
}
